// Normalized part rows for the surfaces that present a whole build.
//
// A build reaches us in two shapes: the live builder keeps it in the menu
// options (store/modules/menu.js), while a saved build comes back from the API
// as a flat record. Both collapse to the same row here so the completed-build
// screen, the saved-build library, and the Amazon cart handoff all read one
// contract instead of each re-deriving names, quantities, and prices.

import { BUILD_PARTS, orderedBuildEntries } from '../partOrder'
import { functions } from '../functions'
import { amazonProductUrl, sanitizeAmazonAsin } from '../config'
import { isPartUnavailable } from './partAvailability'

// Saved-build records carry bookkeeping columns alongside the parts.
export const SAVED_BUILD_FIELD_EXCEPTIONS = [
    'name',
    'accessories',
    'image',
    'id',
    'author_id',
    'cost',
    'status'
]

const PART_ICONS = {
    processor: 'icon-gauge',
    graphics: 'icon-controller-play',
    motherboard: 'icon-grid',
    ram: 'icon-drive',
    storage: 'icon-box',
    case: 'icon-archive',
    psu: 'icon-flash'
}

export function partIcon(key){
    return PART_ICONS[key] || 'icon-tools'
}

export function clockLabel(value){
    var clock = parseFloat(value)
    if(!clock){ return '' }
    if(clock > 1000){ clock = clock / 1000 }
    return clock.toFixed(2) + ' GHz'
}

export function partName(item){
    if(!item){ return '' }
    if(item.type == 'cpu'){
        return item.model || item.brand || 'Processor'
    }
    return [item.brand, item.model].filter(Boolean).join(' ') || item.model || 'Part'
}

// Full selection copy for overflow-only checklist tooltips. Multiple identical
// selections are grouped so a two-GPU build does not repeat the same long name.
export function selectionTooltip(contents){
    var items = Array.isArray(contents) ? contents.filter(Boolean) : (contents ? [contents] : [])
    var counts = {}
    items.forEach((item) => {
        var brand = String(item.brand || '').trim()
        var model = String(item.model || item.name || '').trim()
        var name = model
        if(brand && (!model || model.toLowerCase().indexOf(brand.toLowerCase()) !== 0)){
            name = (brand + ' ' + model).trim()
        }
        if(name){ counts[name] = (counts[name] || 0) + 1 }
    })
    return Object.keys(counts).map((name) => counts[name] > 1 ? counts[name] + ' × ' + name : name).join('\n')
}

export function partSummary(item){
    if(!item){ return '' }
    var type = item.type
    var specs = item.specs || {}
    var compat = item.compat || {}

    if(type == 'cpu'){
        return [
            clockLabel(specs.Core_Frequency),
            specs.Number_of_Cores ? specs.Number_of_Cores + ' cores' : '',
            compat.socket || specs.socket
        ].filter(Boolean).join(' | ')
    }
    if(type == 'gpu' || type == 'graphics'){
        return [
            specs.graphics_ram_capacity ? specs.graphics_ram_capacity + ' GB memory' : '',
            specs.graphics_interface || compat.compat_interface,
            compat.tdp ? compat.tdp + ' W' : ''
        ].filter(Boolean).join(' | ')
    }
    if(type == 'motherboard'){
        return [
            compat.form_factor || specs.Form_Factor,
            compat.socket || specs.socket,
            compat.ram || specs.ram
        ].filter(Boolean).join(' | ')
    }
    if(type == 'ram'){
        return [
            compat.ram_type,
            compat.capacity ? compat.capacity + ' GB' : '',
            compat.ram_speed ? compat.ram_speed + ' MHz' : ''
        ].filter(Boolean).join(' | ')
    }
    if(type == 'storage'){
        return [
            specs.Capacity ? specs.Capacity + ' TB' : '',
            specs.Disk_Type,
            compat.compat_interface
        ].filter(Boolean).join(' | ')
    }
    if(type == 'case'){
        return [
            compat.form_factor,
            specs.color,
            specs.tempered_glass ? 'Tempered glass' : ''
        ].filter(Boolean).join(' | ')
    }
    if(type == 'psu'){
        return [
            specs.wattage ? specs.wattage + ' W' : '',
            specs.energy_rating,
            compat.form_factor
        ].filter(Boolean).join(' | ')
    }
    return ''
}

// The case and the power supply are not loads, and memory draws roughly 3 W per
// 8 GB -- the same convention the builder's running total uses.
export function partTdp(item){
    if(!item){ return 0 }
    if(item.type == 'ram'){
        return 3 * ((parseInt(item.compat && item.compat.capacity) || 0) / 8)
    }
    if(item.type == 'case' || item.type == 'psu'){
        return 0
    }
    return parseInt(item.compat && item.compat.tdp) || 0
}

function formatMoney(value){
    var price = functions.parsePrice(value)
    if(price === null){ return '' }
    return price.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function partRow(entry, item, qty, index){
    var quantity = qty || 0
    var unitPrice = item ? functions.parsePrice(item.amzn_price) : null
    var totalPrice = unitPrice === null ? null : unitPrice * quantity

    return {
        key: entry.key + '-' + index,
        type: entry.key,
        label: entry.label,
        icon: partIcon(entry.key),
        item: item,
        asin: sanitizeAmazonAsin(item && item.asin),
        name: item ? partName(item) : 'Not selected',
        summary: item ? partSummary(item) : '',
        qty: quantity,
        unitPrice: unitPrice === null ? '' : formatMoney(unitPrice),
        price: totalPrice === null ? '' : formatMoney(totalPrice),
        priceBand: totalPrice === null ? '' : functions.formatTypicalPriceBand(totalPrice),
        hasPrice: totalPrice !== null,
        unavailable: isPartUnavailable(item),
        amazonUrl: amazonProductUrl(item && item.asin)
    }
}

// RAM, storage, and graphics hold arrays. Identical listings collapse into one
// row with a quantity so the cart asks for "x2" instead of two separate lines.
function rowsFromArray(entry){
    if(!entry.value || !entry.value.length){
        return [partRow(entry, null, 0, 0)]
    }

    var groups = {}
    var order = []
    entry.value.forEach((item, index) => {
        var key = item && item.id ? item.id : 'item-' + index
        if(!groups[key]){
            groups[key] = { item: item, qty: 0, firstIndex: index }
            order.push(key)
        }
        groups[key].qty++
    })

    return order.map((key) => partRow(entry, groups[key].item, groups[key].qty, groups[key].firstIndex))
}

function rowsForEntry(entry){
    if(Array.isArray(entry.value)){
        return rowsFromArray(entry)
    }
    return [partRow(entry, entry.value, entry.value ? 1 : 0, 0)]
}

export function rowsFromSavedBuild(build){
    var rows = []
    orderedBuildEntries(build, SAVED_BUILD_FIELD_EXCEPTIONS).forEach((entry) => {
        rows = rows.concat(rowsForEntry(entry))
    })
    return rows
}

export function rowsFromMenuOptions(menuOptions){
    var rows = []
    BUILD_PARTS.forEach((part) => {
        var option = menuOptions && menuOptions[part.menuKey]
        var contents = option && option.item ? option.item.contents : null
        rows = rows.concat(rowsForEntry({
            key: part.backendKey,
            label: part.label,
            value: contents === undefined ? null : contents
        }))
    })
    return rows
}

export function summarizeRows(rows){
    var totalCost = 0
    var pricedItemCount = 0
    var missingPriceCount = 0
    var tdp = 0

    ;(rows || []).forEach((row) => {
        if(!row || !row.item){ return }
        var price = functions.parsePrice(row.item.amzn_price)
        if(price !== null){
            totalCost += price * row.qty
            pricedItemCount += row.qty
        }
        else {
            missingPriceCount += row.qty
        }
        tdp += partTdp(row.item) * row.qty
    })

    return {
        totalCost: totalCost,
        pricedItemCount: pricedItemCount,
        missingPriceCount: missingPriceCount,
        tdp: Math.round(tdp)
    }
}

// 160 is the top of the build performance scale (see functions.getBuildPerformance),
// matching how the checklist and the saved-build cards already score a build.
export function performanceTier(score){
    var value = Math.max(0, Math.min(100, Math.round(Number(score) || 0)))
    if(value >= 100){ return { score: value, label: 'High end', className: 'score-high' } }
    if(value >= 67){ return { score: value, label: 'Enthusiast', className: 'score-good' } }
    if(value >= 34){ return { score: value, label: 'Mainstream', className: 'score-mid' } }
    return { score: value, label: 'Entry level', className: 'score-low' }
}
